import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ShieldCheck, FileText, Banknote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { submitTradeInquiry } from "@/lib/api";
import { TRADE_CATEGORIES, BRAND } from "@/lib/constants";
import { whatsappLink } from "@/lib/whatsapp";

const INITIAL = {
  buyer_name: "",
  company_name: "",
  phone: "",
  email: "",
  product_category: "",
  quantity_required: "",
  delivery_city: "",
  additional_requirements: "",
};

export default function Trade() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    document.title = "Trade — OVI";
  }, []);

  const update = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: typeof e === "string" ? e : e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.buyer_name.trim() || form.buyer_name.trim().length < 2) e.buyer_name = "Please enter your name.";
    if (!form.company_name.trim() || form.company_name.trim().length < 2) e.company_name = "Please enter your company name.";
    const cleanedPhone = form.phone.replace(/[\s\-()]/g, "");
    if (!/^\+?\d{7,15}$/.test(cleanedPhone)) e.phone = "Enter a valid phone number.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email.";
    if (!form.product_category) e.product_category = "Please choose a category.";
    if (!form.quantity_required.trim()) e.quantity_required = "Please specify quantity.";
    if (!form.delivery_city.trim() || form.delivery_city.trim().length < 2) e.delivery_city = "Please enter delivery city.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildWhatsAppMessage = (f) =>
    `Hi OVI Trade — I'd like a bulk inquiry.%0A` +
    `• Name: ${f.buyer_name}%0A` +
    `• Company: ${f.company_name}%0A` +
    `• Phone: ${f.phone}%0A` +
    `• Email: ${f.email}%0A` +
    `• Category: ${f.product_category}%0A` +
    `• Quantity: ${f.quantity_required}%0A` +
    `• Delivery city: ${f.delivery_city}%0A` +
    (f.additional_requirements ? `• Notes: ${f.additional_requirements}%0A` : "");

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await submitTradeInquiry(form);
      const waUrl = `https://wa.me/${BRAND.whatsappNumber}?text=${buildWhatsAppMessage(form)}`;
      setSuccess(true);
      toast.success("Inquiry sent. Opening WhatsApp…");
      // Open WhatsApp in a new tab so the buyer can confirm with OVI directly
      try { window.open(waUrl, "_blank", "noopener,noreferrer"); } catch {}
      setForm(INITIAL);
    } catch (err) {
      console.error(err);
      toast.error("Couldn't submit. Please try again or WhatsApp us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[var(--ovi-cream)]">
      {/* Header */}
      <section className="border-b border-[var(--ovi-border)] ovi-texture">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <p className="text-xs uppercase tracking-[0.24em] text-[var(--ovi-terracotta)] font-semibold mb-4">
            OVI Trade · B2B
          </p>
          <h1
            className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[var(--ovi-indigo)] tracking-tight leading-[1.05]"
            data-testid="trade-page-title"
          >
            Source Direct. Buy at Scale.
          </h1>
          <p
            className="mt-6 max-w-3xl text-base sm:text-lg text-[var(--ovi-muted-ink)] leading-relaxed"
            data-testid="trade-page-subline"
          >
            Hotels, retailers, interior designers and corporate buyers — get
            India's finest textiles in bulk, direct from verified manufacturers,
            with GST invoices and quality assurance.
          </p>
        </div>
      </section>

      {/* B2B confidence strip */}
      <section
        className="bg-[rgba(30,42,71,0.06)] border-b border-[var(--ovi-border)]"
        data-testid="trade-confidence-strip"
      >
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
            <ConfidenceStat
              value="₹8,000 Cr+"
              label="Annual textile output from Solapur"
              testid="trade-confidence-output"
            />
            <ConfidenceStat
              value="Direct factory pricing"
              label="No agent markup, no middleman"
              testid="trade-confidence-pricing"
            />
            <ConfidenceStat
              value="GST invoice on every order"
              label="From a registered manufacturer"
              testid="trade-confidence-gst"
            />
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.24em] text-[var(--ovi-terracotta)] font-semibold mb-4">
            Bulk inquiry
          </p>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-[var(--ovi-indigo)]">
            Tell us what you need.
          </h2>
          <p className="mt-3 text-sm sm:text-base text-[var(--ovi-muted-ink)]">
            Our trade team will reply within 1 working day on WhatsApp and email.
          </p>
        </div>

        {success ? (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[var(--ovi-cream-2)] border border-[var(--ovi-border)] rounded-2xl p-8 sm:p-10 text-center"
            data-testid="trade-form-success"
          >
            <CheckCircle2 className="h-10 w-10 text-[var(--ovi-terracotta)] mx-auto" />
            <h3 className="mt-4 font-serif-display text-2xl text-[var(--ovi-indigo)]">
              Inquiry received.
            </h3>
            <p className="mt-2 text-sm sm:text-base text-[var(--ovi-muted-ink)] max-w-md mx-auto">
              We've also opened WhatsApp with a summary so our trade team can pick it up immediately.
            </p>
            <Button
              onClick={() => setSuccess(false)}
              variant="outline"
              className="mt-6 border-[var(--ovi-indigo)] text-[var(--ovi-indigo)] rounded-full h-11 px-6"
              data-testid="trade-form-new"
            >
              Submit another inquiry
            </Button>
          </motion.div>
        ) : (
          <form
            onSubmit={onSubmit}
            noValidate
            data-testid="trade-inquiry-form"
            className="bg-[var(--ovi-cream-2)] border border-[var(--ovi-border)] rounded-2xl p-6 sm:p-8 grid grid-cols-1 md:grid-cols-2 gap-5"
          >
            <Field id="buyer_name" label="Buyer name" error={errors.buyer_name}>
              <Input id="buyer_name" value={form.buyer_name} onChange={update("buyer_name")}
                placeholder="Your full name" className="trade-input"
                data-testid="trade-form-buyer-name-input" autoComplete="name" />
            </Field>
            <Field id="company_name" label="Company name" error={errors.company_name}>
              <Input id="company_name" value={form.company_name} onChange={update("company_name")}
                placeholder="Your business / hotel / brand" className="trade-input"
                data-testid="trade-form-company-name-input" autoComplete="organization" />
            </Field>
            <Field id="phone" label="Phone" error={errors.phone}>
              <Input id="phone" value={form.phone} onChange={update("phone")}
                placeholder="+91 00000 00000" inputMode="tel" className="trade-input"
                data-testid="trade-form-phone-input" autoComplete="tel" />
            </Field>
            <Field id="email" label="Email" error={errors.email}>
              <Input id="email" type="email" value={form.email} onChange={update("email")}
                placeholder="buyer@company.com" className="trade-input"
                data-testid="trade-form-email-input" autoComplete="email" />
            </Field>
            <Field id="product_category" label="Product category" error={errors.product_category}>
              <Select value={form.product_category} onValueChange={update("product_category")}>
                <SelectTrigger id="product_category" className="trade-input h-11"
                  data-testid="trade-form-product-category-select">
                  <SelectValue placeholder="Choose a category" />
                </SelectTrigger>
                <SelectContent className="bg-[var(--ovi-cream-2)] border-[var(--ovi-border)]">
                  {TRADE_CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c} className="focus:bg-[var(--ovi-cream)]"
                      data-testid={`trade-form-category-option-${c.replace(/\s+/g, "-").toLowerCase()}`}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field id="quantity_required" label="Quantity required" error={errors.quantity_required}>
              <Input id="quantity_required" value={form.quantity_required} onChange={update("quantity_required")}
                placeholder="e.g., 500 sheets, 2000 metres" className="trade-input"
                data-testid="trade-form-quantity-input" />
            </Field>
            <Field id="delivery_city" label="Delivery city" error={errors.delivery_city} span="md:col-span-2">
              <Input id="delivery_city" value={form.delivery_city} onChange={update("delivery_city")}
                placeholder="Mumbai, Delhi, Bengaluru…" className="trade-input"
                data-testid="trade-form-delivery-city-input" autoComplete="address-level2" />
            </Field>
            <Field id="additional_requirements" label="Additional requirements" span="md:col-span-2">
              <Textarea id="additional_requirements" value={form.additional_requirements}
                onChange={update("additional_requirements")} rows={4}
                placeholder="Tell us about timelines, sizing, packaging, customisation, etc."
                className="trade-input min-h-[110px]"
                data-testid="trade-form-notes-input" />
            </Field>

            <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
              <p className="text-xs text-[var(--ovi-muted-ink)] max-w-sm">
                We'll get back on WhatsApp and email. GST invoice provided for every order.
              </p>
              <Button type="submit" disabled={submitting}
                className="bg-[var(--ovi-terracotta)] hover:bg-[var(--ovi-terracotta-dark)] text-[var(--ovi-cream-2)] rounded-full h-12 px-7 active:scale-[0.98] transition-[background-color,transform] duration-200 disabled:opacity-70"
                data-testid="trade-form-submit-button">
                {submitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending…</>) : "Send bulk inquiry"}
              </Button>
            </div>
            <p
              className="md:col-span-2 -mt-1 text-xs italic text-[var(--ovi-muted-ink)]"
              data-testid="trade-form-whatsapp-launch-note"
            >
              {BRAND.whatsappLaunchNote}
            </p>
          </form>
        )}
      </section>

      {/* Trust badges */}
      <section className="bg-[var(--ovi-cream-2)] border-t border-[var(--ovi-border)]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
            <TradeBadge
              icon={<Banknote className="h-6 w-6" />}
              title="Minimum order flexible"
              body="From a single carton to a container load. We'll match the right manufacturer to your scale."
              testid="trade-badge-moq"
            />
            <TradeBadge
              icon={<FileText className="h-6 w-6" />}
              title="GST invoice provided"
              body="Every order ships with a proper GST invoice from a registered manufacturer."
              testid="trade-badge-gst"
            />
            <TradeBadge
              icon={<ShieldCheck className="h-6 w-6" />}
              title="Direct manufacturer pricing"
              body="No agents, no commission layers. You get factory pricing with quality assurance from OVI."
              testid="trade-badge-pricing"
            />
          </div>
        </div>
      </section>

      <style>{`
        .trade-input {
          background-color: var(--ovi-cream);
          border-color: var(--ovi-border);
        }
        .trade-input:focus-visible {
          --tw-ring-color: var(--ovi-terracotta);
        }
      `}</style>
    </div>
  );
}

const Field = ({ id, label, error, children, span = "" }) => (
  <div className={`flex flex-col gap-1.5 ${span}`}>
    <Label htmlFor={id} className="text-xs uppercase tracking-[0.18em] font-medium text-[var(--ovi-muted-ink)]">
      {label}
    </Label>
    {children}
    {error ? (
      <p className="text-xs text-[var(--ovi-terracotta)] mt-0.5" data-testid={`trade-form-${id}-error`}>
        {error}
      </p>
    ) : null}
  </div>
);

const TradeBadge = ({ icon, title, body, testid }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.5, ease: [0.2, 0.7, 0.2, 1] }}
    className="bg-[var(--ovi-cream)] border border-[var(--ovi-border)] rounded-2xl p-6 sm:p-7"
    data-testid={testid}
  >
    <div className="h-10 w-10 rounded-full bg-[var(--ovi-terracotta)]/12 text-[var(--ovi-terracotta)] flex items-center justify-center">
      {icon}
    </div>
    <h3 className="mt-4 font-serif-display text-xl sm:text-2xl text-[var(--ovi-indigo)]">{title}</h3>
    <p className="mt-2 text-sm sm:text-base text-[var(--ovi-muted-ink)] leading-relaxed">{body}</p>
  </motion.div>
);

const ConfidenceStat = ({ value, label, testid }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.5, ease: [0.2, 0.7, 0.2, 1] }}
    className="bg-[var(--ovi-cream)] border border-[var(--ovi-border)] rounded-xl px-5 py-5 sm:py-6"
    data-testid={testid}
  >
    <p className="font-serif-display text-2xl sm:text-3xl text-[var(--ovi-indigo)] leading-tight">
      {value}
    </p>
    <p className="mt-2 text-xs sm:text-sm uppercase tracking-[0.16em] text-[var(--ovi-muted-ink)] font-medium">
      {label}
    </p>
  </motion.div>
);
